import { DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, GridColumn } from "semantic-ui-react";

import { Dropdown, useCan, useSelectedDealer, useSelectedLocation } from "components";
import { useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import "modules/ChecklistsDashboard/ChecklistsDashboard.scss";
import { AddChecklistModal, ChecklistsList, EditChecklistModal } from "modules/ChecklistsDashboard/components";
import { ChecklistItem } from "modules/ChecklistsDashboard/components/ChecklistsList/components";
import {
  useAttachChecklistToDealerMutation,
  useAttachChecklistToLocationMutation,
  useDealerChecklistsQuery,
  useDefaultChecklistsQuery,
  useLocationChecklistsQuery,
  useTagsQuery
} from "modules/ChecklistsDashboard/hooks";
import { CHECKLIST_LISTS } from "modules/ChecklistsDashboard/util";

import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import type { Checklist, ChecklistID, TagID } from "models";

const hasAllTags = (checklist: Checklist, tagIds: TagID[]) => tagIds.every(tagId => checklist.tags?.some(tag => tag.id === tagId));

export const ChecklistsDashboard = () => {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 100 } })
  );

  const [selectedTagIds, setSelectedTagIds] = useState<TagID[]>([]);
  const [draggedChecklist, setDraggedChecklist] = useState<Checklist | null>(null);
  const [editedChecklistID, setEditedChecklistID] = useState<ChecklistID>();
  const [editedChecklistList, setEditedChecklistList] = useState(CHECKLIST_LISTS.Location);
  const [addChecklistList, setAddChecklistList] = useState<CHECKLIST_LISTS | null>(null);

  const { selectedLocation } = useSelectedLocation();
  const { selectedDealer } = useSelectedDealer();
  const { data: tagsOptions = [] } = useTagsQuery();
  const { data: locationChecklists = [], isLoading: isLocationChecklistsLoading, refetch: refetchLocationChecklists } = useLocationChecklistsQuery();
  const { data: dealerChecklists = [], isLoading: isDealerChecklistsLoading, refetch: refetchDealerChecklists } = useDealerChecklistsQuery();
  const { data: defaultChecklists = [], isLoading: isDefaultChecklistsLoading, refetch: refetchDefaultChecklists } = useDefaultChecklistsQuery();
  const { mutate: attachChecklistToLocation } = useAttachChecklistToLocationMutation();
  const { mutate: attachChecklistToDealer } = useAttachChecklistToDealerMutation();

  const canAttachToLocation = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.LocationChecklistAttachment);
  const canAttachToDealer = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.DealerChecklistAttachment);
  const canListLocations = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.Locations);
  const canListDealers = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.Dealers);

  useRegisterNavAction(() => {
    if (canListLocations) refetchLocationChecklists();
    if (canListDealers) refetchDealerChecklists();
    refetchDefaultChecklists();
  });

  const filteredLocationChecklists = locationChecklists.filter(lc => hasAllTags(lc, selectedTagIds));
  const filteredDealerChecklists = dealerChecklists.filter(dc => hasAllTags(dc, selectedTagIds));
  const filteredDefaultChecklists = defaultChecklists.filter(
    defaultChecklist => !dealerChecklists.some(dc => dc.id === defaultChecklist.id) && hasAllTags(defaultChecklist, selectedTagIds)
  );

  const handleChecklistClick = (checklistID: ChecklistID, list: CHECKLIST_LISTS) => {
    setEditedChecklistID(checklistID);
    setEditedChecklistList(list);
  };

  const handleChecklistCreated = (checklistID: ChecklistID) => {
    setAddChecklistList(null);
    if (!addChecklistList) return;

    setEditedChecklistID(checklistID);
    setEditedChecklistList(addChecklistList);
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    setDraggedChecklist([...dealerChecklists, ...defaultChecklists].find(checklist => checklist.id === active.id) ?? null);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setDraggedChecklist(null);
    if (!over || typeof active.id !== "number" || over.id === active.data.current?.list) return;

    if (over.id === CHECKLIST_LISTS.Location && selectedLocation) attachChecklistToLocation({ checklist_id: active.id, dealer_location_id: selectedLocation.id });
    if (over.id === CHECKLIST_LISTS.Dealer && selectedDealer) attachChecklistToDealer({ checklist_id: active.id, dealer_id: selectedDealer.id });
  };

  return (
    <div className="ChecklistsDashboard">
      <Dropdown
        multiple
        selection
        search
        fluid
        className="checklists-tags-filter"
        placeholder={t("v8_filter_by_tags", "Filter by tags")}
        options={tagsOptions}
        value={selectedTagIds}
        onChange={setSelectedTagIds}
      />

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setDraggedChecklist(null)}>
        <Grid columns={3}>
          <GridColumn>
            <ChecklistsList
              list={CHECKLIST_LISTS.Location}
              title={t("v8_location_checklists", "Location Checklists")}
              checklists={filteredLocationChecklists}
              isLoading={isLocationChecklistsLoading}
              isDroppable={canAttachToLocation}
              onChecklistClick={handleChecklistClick}
            />
          </GridColumn>
          <GridColumn>
            <ChecklistsList
              list={CHECKLIST_LISTS.Dealer}
              title={t("v8_dealer_checklists", "Dealer Checklists")}
              checklists={filteredDealerChecklists}
              isLoading={isDealerChecklistsLoading}
              isDroppable={canAttachToDealer}
              addResource={ACL_RESOURCES.Checklists}
              onAdd={() => setAddChecklistList(CHECKLIST_LISTS.Dealer)}
              onChecklistClick={handleChecklistClick}
            />
          </GridColumn>
          <GridColumn>
            <ChecklistsList
              list={CHECKLIST_LISTS.Default}
              title={t("v8_default_checklists", "Default Checklists")}
              checklists={filteredDefaultChecklists}
              isLoading={isDefaultChecklistsLoading}
              addResource={ACL_RESOURCES.DefaultChecklists}
              onAdd={() => setAddChecklistList(CHECKLIST_LISTS.Default)}
              onChecklistClick={handleChecklistClick}
            />
          </GridColumn>
        </Grid>

        <DragOverlay dropAnimation={null}>{draggedChecklist && <ChecklistItem checklist={draggedChecklist} />}</DragOverlay>
      </DndContext>

      <AddChecklistModal
        isOpen={addChecklistList !== null}
        isDealerChecklist={addChecklistList === CHECKLIST_LISTS.Dealer}
        onClose={() => setAddChecklistList(null)}
        onCreate={handleChecklistCreated}
      />

      <EditChecklistModal
        checklistID={editedChecklistID}
        currentList={editedChecklistList}
        onClose={() => setEditedChecklistID(undefined)}
        onOpenChecklist={setEditedChecklistID}
      />
    </div>
  );
};
